import React from 'react'
import { DocumentDownload } from 'iconsax-react'
import styled from 'styled-components'

import IconButton, {
  SIZES as ICON_BUTTON_SIZES,
  VARIANTS as ICON_BUTTON_VARIANTS,
} from 'components/lib/DesignSystem/V3/IconButton'
import Close from 'components/lib/DesignSystem/V3/icons/Close'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'

const Bar = styled.div`
  /* Same bar as the training report's participants table, so bulk actions read the
     same across the awareness area: primary hairline, soft primary halo, 10px radius. */
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--t-spacing-4);
  min-height: 48px;
  padding: var(--t-spacing-2) var(--t-spacing-3);
  margin-bottom: var(--t-spacing-3);
  background: var(--color-surface);
  border: 1px solid var(--primary);
  border-radius: var(--t-border-radius-lg);
  box-shadow:
    0 0 0 4px var(--t-opacity-primary-10, rgba(48, 99, 253, 0.1)),
    0 8px 20px -6px rgba(48, 99, 253, 0.22);
`

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: var(--t-spacing-3);
  flex-wrap: wrap;
`

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: var(--t-spacing-6);
  height: var(--t-spacing-6);
  padding: 0 var(--t-spacing-2);
  border-radius: var(--t-border-radius-rounded);
  background: var(--primary);
  color: var(--white-fixed, #fff);
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-medium);
`

const SelectedLabel = styled.span`
  color: var(--color-text);
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-medium);
`

const Divider = styled.span`
  width: 1px;
  height: var(--t-spacing-4);
  background: var(--color-border, var(--card-border-color));
`

const TotalLabel = styled.span`
  color: var(--sub-text-color);
  font-size: var(--t-typography-font-size-sm);
`

/* V3 has no link-style button yet, so this is the smallest token-built stand-in rather
   than the router-bound V1 Link, which expects an href. */
const LinkAction = styled.button`
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--t-typography-font-size-sm);
  font-weight: var(--t-typography-font-weight-medium);
  color: var(--link-text-default);

  &:hover,
  &:focus-visible {
    color: var(--link-text-hover);
    text-decoration: underline;
  }
`

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--t-spacing-2);
`

/**
 * Bulk-selection bar for the launched list, following the training-report pattern:
 * how many are selected on the left with a way to extend the selection, actions on the
 * right, and a close that drops the selection entirely.
 */
const SelectionBar = ({
  selectedCount,
  totalCount,
  allMatchingSelected,
  onSelectAllMatching,
  onClear,
  onDownloadCsv,
}) => (
  <Bar data-testid="Psima_Launched_SelectionBar_div">
    <Left>
      <CountBadge data-testid="Psima_Launched_SelectionBar_Count_span">{selectedCount}</CountBadge>
      <SelectedLabel>selected</SelectedLabel>
      <Divider />
      <TotalLabel>
        {totalCount === 1 ? 'of 1 simulation' : `of ${totalCount} simulations`}
      </TotalLabel>
      {!allMatchingSelected && (
        <LinkAction
          type="button"
          data-testid="Psima_Launched_SelectAllMatching_Button"
          onClick={onSelectAllMatching}
        >
          {`Select all ${totalCount} matching`}
        </LinkAction>
      )}
    </Left>
    <Right>
      <Tooltip
        id="Psima_Launched_DownloadCsv_tooltip"
        side="top"
        arrow
        content="Download CSV of the selection"
      >
        <IconButton
          id="Psima_Launched_DownloadCsv_Button"
          ariaLabel="Download CSV of the selection"
          dataTestId="Psima_Launched_DownloadCsv_Button"
          size={ICON_BUTTON_SIZES.MEDIUM}
          variant={ICON_BUTTON_VARIANTS.GHOST}
          onClick={onDownloadCsv}
        >
          <DocumentDownload size={16} variant="Outline" />
        </IconButton>
      </Tooltip>
      <Tooltip
        id="Psima_Launched_ClearSelection_tooltip"
        side="top"
        arrow
        content="Cancel selection"
      >
        <IconButton
          id="Psima_Launched_ClearSelection_Button"
          ariaLabel="Cancel selection"
          dataTestId="Psima_Launched_ClearSelection_Button"
          size={ICON_BUTTON_SIZES.MEDIUM}
          variant={ICON_BUTTON_VARIANTS.GHOST}
          onClick={onClear}
        >
          <Close width={14} height={14} />
        </IconButton>
      </Tooltip>
    </Right>
  </Bar>
)

export default SelectionBar
